import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import isNumber from 'lodash/isNumber'
import AnalyticsService from '../../../services/analytics-service'
import { useDateFormatter } from '../../../hooks/date'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import { pdpPageEventLabels } from '../../../services/analytics-service/labels'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from './use-searchbar'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import { PdpToCheckoutPosition } from '../constants'
import { usePropertyDetailsContext } from '../../../hooks/property-details'
import { getCategory } from '../../../utils/category-utils'

interface UseDateGuestPickerProps {
    searchbar: SearchbarFieldsReturn
    updateSearchbar: UpdateSearchBarTransport
    pricingDetails: FormattedLivePricingDataTransport | null
    position:
        | PdpToCheckoutPosition.GALLERY
        | PdpToCheckoutPosition.SIDEBAR
        | PdpToCheckoutPosition.STICKY_FOOTER
        | PdpToCheckoutPosition.PRICE_WIDGET
        | PdpToCheckoutPosition.CONTENT
        | PdpToCheckoutPosition.GALLERY_FOOTER
        | PdpToCheckoutPosition.CONTACT_BUTTON
}

const useDateGuestPicker = ({ searchbar, updateSearchbar, pricingDetails, position }: UseDateGuestPickerProps) => {
    const [isDatePickerOpen, setDatePickerOpen] = useState(false)
    const [isPersonSelectorOpen, setPersonSelectorOpen] = useState(false)
    const toggleDatePicker = useCallback((isOpen: boolean) => setDatePickerOpen(isOpen), [])
    const togglePersonSelector = useCallback((isOpen: boolean) => setPersonSelectorOpen(isOpen), [])
    const autoCorrectionRef = useRef<number>(1)
    const { format } = useDateFormatter()
    const { isComboListing } = usePropertyDetailsContext()

    useEffect(() => {
        if (isNumber(pricingDetails?.autoCorrection?.adults)) {
            autoCorrectionRef.current = pricingDetails.autoCorrection.adults
        }
    }, [pricingDetails])

    const handleSearchbarUpdate = useCallback(
        (field: keyof SearchbarFieldsReturn, value: any) => {
            updateSearchbar(field, value)
        },
        [updateSearchbar],
    )

    const trackArrivalDateChange = useCallback(
        (startDate: Date | null, endDate?: Date | null) => {
            if (startDate) {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.DATE_PICKER_START_DATE_CHANGED,
                    label:
                        position === PdpToCheckoutPosition.CONTENT
                            ? pdpPageEventLabels.WIDGETS.PRICE_WIDGET
                            : pdpPageEventLabels.WIDGETS.SIDEBAR,
                    value: format(startDate, 'dd.MM.yyyy'),
                })
                if (endDate) {
                    AnalyticsService.trackEvent({
                        category: getCategory(isComboListing),
                        action: pdpPageActions.DATES_SELECTED,
                        value: `${format(startDate, 'dd.MM.yyyy')}_${format(endDate, 'dd.MM.yyyy')}`,
                    })
                }
            }
        },
        [format, position, isComboListing],
    )

    const trackDepartureDateChange = useCallback(
        (startDate: Date | null, endDate?: Date | null) => {
            if (endDate) {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.DATE_PICKER_END_DATE_CHANGED,
                    label:
                        position === PdpToCheckoutPosition.CONTENT
                            ? pdpPageEventLabels.WIDGETS.PRICE_WIDGET
                            : pdpPageEventLabels.WIDGETS.SIDEBAR,
                    value: format(endDate, 'dd.MM.yyyy'),
                })
                if (startDate) {
                    AnalyticsService.trackEvent({
                        category: getCategory(isComboListing),
                        action: pdpPageActions.DATES_SELECTED,
                        value: `${format(startDate, 'dd.MM.yyyy')}_${format(endDate, 'dd.MM.yyyy')}`,
                    })
                }
            }
        },
        [format, position, isComboListing],
    )

    const handleArrivalDateChange = useCallback(
        (date: Date | null) => handleSearchbarUpdate('arrival', date),
        [handleSearchbarUpdate],
    )

    const handleDepartureDateChange = useCallback(
        (date: Date | null) => {
            handleSearchbarUpdate('departure', date)
            if (searchbar?.arrival && date) {
                setDatePickerOpen(false)
            }
        },
        [handleSearchbarUpdate, searchbar?.arrival],
    )

    const handleAdultsChange = useCallback(
        (count: number) => {
            handleSearchbarUpdate('adults', count)
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.ADULTS_COUNT_CHANGED,
                value: count.toString(),
            })
        },
        [handleSearchbarUpdate, isComboListing],
    )

    const handleKidsChange = useCallback(
        (count: number) => {
            handleSearchbarUpdate('kids', count)
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.CHILDREN_COUNT_CHANGED,
                value: count.toString(),
            })
        },
        [handleSearchbarUpdate, isComboListing],
    )

    const handlePetsChange = useCallback(
        (count: number) => {
            handleSearchbarUpdate('pets', count)
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.PETS_COUNT_CHANGED,
                value: count.toString(),
            })
        },
        [handleSearchbarUpdate, isComboListing],
    )

    const adultsCount = useMemo(() => Math.max(searchbar.adults, autoCorrectionRef.current), [searchbar.adults])

    // === Toggle Helpers ===
    const openPersonSelector = useCallback(() => togglePersonSelector(true), [togglePersonSelector])
    const closePersonSelector = useCallback(() => togglePersonSelector(false), [togglePersonSelector])
    const openDateSelector = useCallback(() => toggleDatePicker(true), [toggleDatePicker])
    const closeDateSelector = useCallback(() => toggleDatePicker(false), [toggleDatePicker])

    return {
        // States
        isDatePickerOpen,
        isPersonSelectorOpen,
        autoCorrectionRef,
        adultsCount,

        // Toggle helpers
        openDateSelector,
        closeDateSelector,
        openPersonSelector,
        closePersonSelector,

        // Handlers
        handleArrivalDateChange,
        handleDepartureDateChange,
        handleAdultsChange,
        handleKidsChange,
        handlePetsChange,
        trackArrivalDateChange,
        trackDepartureDateChange,
    }
}

export default useDateGuestPicker
